// Tracks ideas the user has explicitly rejected. We keep the last N entries
// in localStorage and ship them back to the worker on every spin so the LLM
// can avoid generating anything similar.
//
// We store only title + subtitle (the discriminating shape), the whole
// IdeaDTO would bloat localStorage and is overkill for dedup.

import { readJson, writeJson } from "./storage";

const KEY = "saaslots:rejected_v1";
const MAX_ENTRIES = 20;

export interface RejectedIdea {
  title: string;
  subtitle?: string;
  /** Optional one-line "why I hated it" the user might write later. */
  reason?: string;
  /** Unix seconds. Lets us age out old entries if needed. */
  at: number;
}

function readAll(): RejectedIdea[] {
  const raw = readJson<unknown>(KEY, []);
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is RejectedIdea =>
      !!x && typeof (x as RejectedIdea).title === "string",
  );
}

function writeAll(entries: RejectedIdea[]): void {
  writeJson(KEY, entries);
}

/** Newest first. Caller can slice for the prompt payload. */
export function listRejected(): RejectedIdea[] {
  return readAll().sort((a, b) => b.at - a.at);
}

/** Adds an entry. De-dupes by title (case-insensitive). */
export function addRejected(entry: Omit<RejectedIdea, "at"> & { at?: number }): void {
  const cur = readAll();
  const key = entry.title.trim().toLowerCase();
  const deduped = cur.filter((x) => x.title.trim().toLowerCase() !== key);
  deduped.unshift({
    title: entry.title.trim(),
    subtitle: entry.subtitle?.trim(),
    reason: entry.reason?.trim(),
    at: entry.at ?? Math.floor(Date.now() / 1000),
  });
  writeAll(deduped.slice(0, MAX_ENTRIES));
}

export function clearRejected(): void {
  writeAll([]);
}
